import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowRight, Download, Filter, RefreshCcw, TrendingUp, Users } from 'lucide-react';

/**
 * Página Trazabilidad — histórico indefinido de cambios de centro por empleado.
 * Se detecta automáticamente en el sync SFTP diario cuando cambia el
 * `desc_centro_coste` de un empleado entre snapshots.
 *
 * Endpoints backend usados:
 *   - GET /api/traceability          (lista paginada + filtros)
 *   - GET /api/traceability/summary  (KPIs agregados)
 *   - GET /api/traceability/export   (xlsx/csv)
 */
export default function TrazabilidadPage() {
  const { apiFetch } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = (() => {
    const d = new Date(); d.setDate(d.getDate() - 90);
    return d.toISOString().slice(0, 10);
  })();

  const [dateFrom, setDateFrom] = useState(monthAgo);
  const [dateTo,   setDateTo]   = useState(today);
  const [search,   setSearch]   = useState('');
  const [centro,   setCentro]   = useState('');
  const [centros,  setCentros]  = useState([]);
  const [rows,     setRows]     = useState([]);
  const [total,    setTotal]    = useState(0);
  const [pages,    setPages]    = useState(1);
  const [page,     setPage]     = useState(1);
  const [summary,  setSummary]  = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [exporting, setExporting] = useState(false);

  // Cargar centros disponibles (RLS-aware)
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await apiFetch('/api/kpis/centros');
        if (!r.ok) return;
        const data = await r.json();
        if (!cancel) setCentros(Array.isArray(data) ? data : []);
      } catch (_) { /* ignore */ }
    })();
    return () => { cancel = true; };
  }, [apiFetch]);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, per_page: 50 });
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo)   params.append('date_to', dateTo);
      if (search)   params.append('search', search);
      if (centro)   params.append('centro', centro);

      const [rL, rS] = await Promise.all([
        apiFetch(`/api/traceability?${params}`),
        apiFetch(`/api/traceability/summary?${new URLSearchParams(
          Object.fromEntries(Object.entries({ date_from: dateFrom, date_to: dateTo }).filter(([, v]) => v))
        )}`),
      ]);
      const listData = await rL.json();
      const sumData  = await rS.json();
      setRows(listData?.data || []);
      setTotal(listData?.total || 0);
      setPages(listData?.pages || 1);
      setPage(p);
      setSummary(sumData || null);
    } catch (e) {
      toast.error('Error al cargar trazabilidad');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, dateFrom, dateTo, search, centro]);

  useEffect(() => { load(1); /* on mount */ }, []);

  const handleFilter = (e) => { e.preventDefault(); load(1); };

  const handleExport = async (fmt) => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ format: fmt });
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo)   params.append('date_to', dateTo);
      if (search)   params.append('search', search);
      if (centro)   params.append('centro', centro);
      const res = await apiFetch(`/api/traceability/export?${params}`);
      if (!res.ok) {
        toast.error(`Error ${res.status} al exportar`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `trazabilidad_${dateFrom || 'all'}_${dateTo || 'all'}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exportado: ${a.download}`);
    } catch (e) {
      toast.error('Error al exportar');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso + (iso.includes('T') ? '' : 'T00:00:00'));
      return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return iso; }
  };

  return (
    <div data-testid="trazabilidad-page" style={{ padding: '32px', overflowY: 'auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ fontFamily: 'Space Grotesk', fontSize: '24px', fontWeight: 700, color: '#f1f5f9', margin: 0 }}>
          Trazabilidad de contratos
        </h1>
        <p style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>
          Histórico indefinido de cambios de centro / contrato por empleado. Los cambios se detectan automáticamente en cada sincronización SFTP.
        </p>
      </div>

      {/* KPI cards */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '20px' }}>
          <StatCard icon={<TrendingUp size={18} />} label="Transiciones detectadas" value={summary.total_transiciones} color="#60a5fa" testId="trace-stat-total" />
          <StatCard icon={<Users size={18} />} label="Empleados con cambios" value={summary.empleados_unicos} color="#a78bfa" testId="trace-stat-empleados" />
          <TopCentrosCard title="Top destinos" items={summary.top_destino} color="#4ade80" testId="trace-stat-destinos" />
          <TopCentrosCard title="Top orígenes" items={summary.top_origen} color="#fbbf24" testId="trace-stat-origenes" />
        </div>
      )}

      {/* Filters */}
      <form onSubmit={handleFilter} style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', padding: '16px', marginBottom: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', alignItems: 'end' }}>
        <div>
          <Label style={{ color: '#94a3b8', fontSize: '11px' }}>Desde</Label>
          <Input data-testid="trace-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', marginTop: 4 }} />
        </div>
        <div>
          <Label style={{ color: '#94a3b8', fontSize: '11px' }}>Hasta</Label>
          <Input data-testid="trace-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', marginTop: 4 }} />
        </div>
        <div>
          <Label style={{ color: '#94a3b8', fontSize: '11px' }}>Buscar (nombre/PNR/DNI)</Label>
          <Input data-testid="trace-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="ej. 82324 o Garcia" style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', marginTop: 4 }} />
        </div>
        <div>
          <Label style={{ color: '#94a3b8', fontSize: '11px' }}>Centro</Label>
          <select
            data-testid="trace-centro-select"
            value={centro}
            onChange={(e) => setCentro(e.target.value)}
            style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', borderRadius: 8, padding: '8px 10px', width: '100%', marginTop: 4, fontSize: 13 }}
          >
            <option value="">Todos</option>
            {centros.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
          <Button type="submit" data-testid="trace-apply-filters" disabled={loading} style={{ background: '#2563eb', color: 'white', height: 36, gap: 6, display: 'flex', alignItems: 'center' }}>
            <Filter size={14} /> Aplicar
          </Button>
          <Button type="button" onClick={() => load(page)} data-testid="trace-refresh" variant="outline" disabled={loading} style={{ height: 36, gap: 6, display: 'flex', alignItems: 'center', background: '#0f172a', color: '#94a3b8', border: '1px solid #334155' }}>
            <RefreshCcw size={14} />
          </Button>
        </div>
      </form>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <Button onClick={() => handleExport('xlsx')} disabled={exporting || total === 0} data-testid="trace-export-xlsx" style={{ background: '#065f46', color: '#d1fae5', height: 34, gap: 6, display: 'flex', alignItems: 'center' }}>
          <Download size={14} /> Exportar XLSX
        </Button>
        <Button onClick={() => handleExport('csv')} disabled={exporting || total === 0} data-testid="trace-export-csv" variant="outline" style={{ background: '#0f172a', border: '1px solid #334155', color: '#94a3b8', height: 34, gap: 6, display: 'flex', alignItems: 'center' }}>
          <Download size={14} /> CSV
        </Button>
        <span style={{ color: '#64748b', fontSize: 12, alignSelf: 'center' }}>
          {total.toLocaleString('es-ES')} transición(es)
        </span>
      </div>

      {/* Table */}
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '12px', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#0f172a', color: '#94a3b8', textAlign: 'left', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                <th style={{ padding: '12px' }}>Fecha detección</th>
                <th style={{ padding: '12px' }}>Empleado</th>
                <th style={{ padding: '12px' }}>DNI</th>
                <th style={{ padding: '12px' }}>Nº Personal</th>
                <th style={{ padding: '12px' }}>Origen</th>
                <th style={{ padding: '12px' }}></th>
                <th style={{ padding: '12px' }}>Destino</th>
                <th style={{ padding: '12px' }}>Fuente</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>Cargando…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: '48px', textAlign: 'center', color: '#475569' }}>
                  Sin transiciones detectadas en el rango seleccionado. Se rellenará automáticamente cuando el sync detecte cambios de centro.
                </td></tr>
              ) : rows.map((r) => (
                <tr key={r._id} data-testid={`trace-row-${r._id}`} style={{ borderTop: '1px solid #0f172a', color: '#cbd5e1' }}>
                  <td style={{ padding: '10px 12px', fontFamily: 'IBM Plex Mono', color: '#94a3b8', whiteSpace: 'nowrap' }}>{formatDate(r.detected_at)}</td>
                  <td style={{ padding: '10px 12px', color: '#f1f5f9' }}>
                    {(r.apellidos || '') + (r.nombre ? `, ${r.nombre}` : '')}
                  </td>
                  <td style={{ padding: '10px 12px', fontFamily: 'IBM Plex Mono', color: '#64748b' }}>{r.dni || '—'}</td>
                  <td style={{ padding: '10px 12px', fontFamily: 'IBM Plex Mono', color: '#64748b' }}>#{r.numero_personal}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ background: 'rgba(251,191,36,0.12)', color: '#fbbf24', padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600 }}>
                      {r.from_centro || '—'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', color: '#475569' }}><ArrowRight size={14} /></td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ background: 'rgba(74,222,128,0.12)', color: '#4ade80', padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600 }}>
                      {r.to_centro || '—'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 11, color: '#64748b', fontFamily: 'IBM Plex Mono' }}>{r.source || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginTop: '20px' }}>
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => load(page - 1)} data-testid="trace-prev" style={{ background: '#1e293b', border: '1px solid #334155', color: page <= 1 ? '#334155' : '#94a3b8' }}>Anterior</Button>
          <span style={{ color: '#64748b', fontSize: 13 }}>Página {page} de {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => load(page + 1)} data-testid="trace-next" style={{ background: '#1e293b', border: '1px solid #334155', color: page >= pages ? '#334155' : '#94a3b8' }}>Siguiente</Button>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, color, testId }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={testId}
      style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 6 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color }}>
        {icon}
        <span style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
      </div>
      <div style={{ color, fontSize: 26, fontWeight: 700, fontFamily: 'Space Grotesk' }}>
        {(value ?? 0).toLocaleString('es-ES')}
      </div>
    </motion.div>
  );
}

function TopCentrosCard({ title, items, color, testId }) {
  return (
    <Card data-testid={testId} style={{ background: '#1e293b', border: '1px solid #334155', padding: 14 }}>
      <div style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>{title}</div>
      {(items || []).length === 0 ? (
        <div style={{ color: '#475569', fontSize: 12 }}>—</div>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {items.slice(0, 4).map((it, i) => (
            <li key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#cbd5e1', fontSize: 12 }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '75%' }}>{it.centro}</span>
              <span style={{ color, fontWeight: 700 }}>{it.n}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
